import { http, AUTH_TOKEN_STORAGE_KEY } from '@/api/http'
import type { User } from '@/lib/pos-types'
import { normalizeUser } from '@/api/normalizers'

type LoginResponse = {
  token: string
  user: unknown
}

const getStorage = () => (typeof localStorage !== 'undefined' ? localStorage : null)

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null

const pickLoginPayload = (payload: unknown): { token: string; user: unknown } | null => {
  const obj = asRecord(payload)
  if (!obj) return null

  const tokenDirect = obj.token
  const userDirect = obj.user
  if (typeof tokenDirect === 'string' && userDirect !== undefined) return { token: tokenDirect, user: userDirect }

  const dataObj = asRecord(obj.data)
  if (!dataObj) return null
  const tokenNested = dataObj.token
  const userNested = dataObj.user
  if (typeof tokenNested === 'string' && userNested !== undefined) return { token: tokenNested, user: userNested }

  return null
}

const pickUserPayload = (payload: unknown): unknown => {
  const obj = asRecord(payload)
  if (!obj) return payload
  if (obj.user !== undefined) return obj.user
  const dataObj = asRecord(obj.data)
  if (dataObj?.user !== undefined) return dataObj.user
  return payload
}

const extractErrorMessage = (error: unknown): string | null => {
  const err = asRecord(error)
  const response = err ? asRecord(err.response) : null
  const data = response ? asRecord(response.data) : null
  const message = data?.message
  return typeof message === 'string' && message.trim() ? message : null
}

export const authApi = {
  login: async (email: string, password: string): Promise<User> => {
    try {
      const form = new URLSearchParams()
      form.set('email', email)
      form.set('password', password)
      const res = await http.post<unknown>('/auth/login', form, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      })
      const parsed = pickLoginPayload(res.data) ?? (res.data as LoginResponse)
      const token = String(parsed.token ?? '')
      if (!token) throw new Error('Token inválido')
      getStorage()?.setItem(AUTH_TOKEN_STORAGE_KEY, token)
      return normalizeUser(parsed.user)
    } catch (e) {
      throw new Error(extractErrorMessage(e) ?? 'No se pudo iniciar sesión')
    }
  },
  me: async (): Promise<User> => {
    const res = await http.get<unknown>('/auth/me')
    return normalizeUser(pickUserPayload(res.data))
  },
  logout: async (): Promise<void> => {
    try {
      await http.post('/auth/logout')
    } catch {
      // ignore
    } finally {
      getStorage()?.removeItem(AUTH_TOKEN_STORAGE_KEY)
    }
  },
  getToken: () => getStorage()?.getItem(AUTH_TOKEN_STORAGE_KEY) ?? null,
  clearToken: () => getStorage()?.removeItem(AUTH_TOKEN_STORAGE_KEY),
}
